/**
 * Agency Animation Engine — Full rebuild
 * GSAP 3.12.5 + ScrollTrigger + Lenis 1.1.9
 * Features: scroll animations, smooth scroll, marquee, cursor, preloader,
 *           navigation, micro-interactions, scroll-linked video, lazy blur
 * Editor-safe, reduced-motion safe, touch-device safe.
 */
(function () {
  'use strict';

  /* ================================================================
   * CDN + UTILITIES
   * ================================================================ */
  var CDN_GSAP = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
  var CDN_ST   = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';
  var CDN_LEN  = 'https://cdn.jsdelivr.net/npm/lenis@1.1.9/dist/lenis.min.js';

  var isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function loadScript(src) {
    return new Promise(function (ok, fail) {
      if (document.querySelector('script[src="' + src + '"]')) { ok(); return; }
      var s = document.createElement('script');
      s.src = src; s.onload = ok; s.onerror = fail;
      document.head.appendChild(s);
    });
  }

  /* ── Text splitting helpers ── */
  function splitChars(el) {
    var t = el.textContent; el.innerHTML = '';
    el.setAttribute('aria-label', t);
    t.split('').forEach(function (c) {
      var s = document.createElement('span');
      s.className = 'char';
      s.textContent = c === ' ' ? '\u00A0' : c;
      s.style.display = 'inline-block';
      s.setAttribute('aria-hidden', 'true');
      el.appendChild(s);
    });
    return el.querySelectorAll('.char');
  }

  function splitWords(el) {
    var t = el.textContent; el.innerHTML = '';
    el.setAttribute('aria-label', t);
    t.split(' ').forEach(function (w, i, arr) {
      var s = document.createElement('span');
      s.className = 'word'; s.textContent = w;
      s.style.display = 'inline-block';
      s.setAttribute('aria-hidden', 'true');
      el.appendChild(s);
      if (i < arr.length - 1) el.appendChild(document.createTextNode(' '));
    });
    return el.querySelectorAll('.word');
  }

  function splitLines(el) {
    var wrap = document.createElement('div');
    wrap.className = 'line-wrapper';
    wrap.style.overflow = 'hidden';
    wrap.style.display = 'block';
    var inner = document.createElement('div');
    inner.className = 'line';
    inner.textContent = el.textContent;
    wrap.appendChild(inner);
    el.textContent = '';
    el.appendChild(wrap);
    return inner;
  }

  /* ================================================================
   * MODULE 1 — SMOOTH SCROLLING (Lenis)
   * ================================================================ */
  function initLenis(gsap, ScrollTrigger) {
    var Lenis = window.Lenis;
    if (!Lenis || typeof Lenis !== 'function' || reducedMotion) return null;
    try {
      var lenis = new Lenis({
        lerp: 0.08,
        smoothWheel: true,
        wheelMultiplier: 1
      });
      ScrollTrigger.scrollerProxy(document.body, {
        scrollTop: function (v) {
          if (arguments.length) lenis.scrollTo(v);
          return lenis.scroll;
        },
        getBoundingClientRect: function () {
          return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
        }
      });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      window.__lenisInstance = lenis;
      return lenis;
    } catch (e) {
      console.warn('Lenis init error:', e);
      return null;
    }
  }

  /* ================================================================
   * MODULE 2 — PRELOADER
   * ================================================================ */
  function initPreloader(gsap) {
    var el = document.querySelector('[data-preloader]');
    if (!el) return;
    if (reducedMotion) { el.classList.add('is-hidden'); return; }
    gsap.to(el, {
      opacity: 0,
      duration: 0.5,
      delay: 0.4,
      onComplete: function () { el.classList.add('is-hidden'); }
    });
  }

  /* ================================================================
   * MODULE 3 — CUSTOM CURSOR
   * ================================================================ */
  function initCursor(gsap) {
    if (isTouch || reducedMotion) return;
    var cursor = document.querySelector('[data-cursor]');
    if (!cursor) return;

    var dot = cursor.querySelector('.agency-cursor__dot');
    var outline = cursor.querySelector('.agency-cursor__outline');
    var stickerEl = cursor.querySelector('[data-cursor-sticker]');
    if (!dot || !outline) return;

    /* Position via left/top with gsap.quickTo for smooth lag */
    var dotX = gsap.quickTo(dot, 'left', { duration: 0.12, ease: 'power2.out' });
    var dotY = gsap.quickTo(dot, 'top', { duration: 0.12, ease: 'power2.out' });
    var outX = gsap.quickTo(outline, 'left', { duration: 0.4, ease: 'power3.out' });
    var outY = gsap.quickTo(outline, 'top', { duration: 0.4, ease: 'power3.out' });

    window.addEventListener('mousemove', function (e) {
      dotX(e.clientX); dotY(e.clientY);
      outX(e.clientX); outY(e.clientY);
    });

    /* Hover detection via event delegation */
    var hoverSel = 'a, button, [data-magnetic], [data-hover-scale], [data-tilt], input, textarea, select, .agency-services__card, .agency-resources__card, .agency-btn';
    document.body.addEventListener('mouseover', function (e) {
      if (e.target.closest(hoverSel)) {
        cursor.classList.add('is-hover');
        var src = e.target.closest('[data-cursor-sticker]');
        if (src && stickerEl) {
          stickerEl.textContent = src.dataset.cursorSticker;
          cursor.classList.add('is-sticker');
        }
      }
    });
    document.body.addEventListener('mouseout', function (e) {
      if (e.target.closest(hoverSel)) {
        cursor.classList.remove('is-hover', 'is-sticker');
        if (stickerEl) stickerEl.textContent = '';
      }
    });
  }

  /* ================================================================
   * MODULE 4 — SCROLL PROGRESS BAR
   * ================================================================ */
  function initScrollProgress(gsap, ScrollTrigger) {
    var bars = gsap.utils.toArray('[data-scroll-progress]');
    if (!bars.length) return;
    bars.forEach(function (bar) {
      gsap.to(bar, {
        scaleX: 1, ease: 'none',
        scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: 0.3 }
      });
    });
  }

  /* ================================================================
   * MODULE 5 — SCROLL ANIMATIONS (data-anim system)
   * ================================================================ */
  function initScrollAnimations(gsap, ScrollTrigger) {

    /* ── headline ── */
    gsap.utils.toArray('[data-anim="headline"]').forEach(function (el) {
      gsap.fromTo(el,
        { opacity: 0, y: 30, filter: 'blur(8px)' },
        { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' }
        });
    });

    /* ── text-reveal (char by char) ── */
    gsap.utils.toArray('[data-anim="text-reveal"]').forEach(function (el) {
      var chars = splitChars(el);
      gsap.fromTo(chars,
        { opacity: 0, y: 50, rotateX: -90 },
        { opacity: 1, y: 0, rotateX: 0, duration: 0.8, stagger: 0.02, ease: 'back.out(1.7)',
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' }
        });
    });

    /* ── word-reveal ── */
    gsap.utils.toArray('[data-anim="word-reveal"]').forEach(function (el) {
      var words = splitWords(el);
      gsap.fromTo(words,
        { opacity: 0, y: 30, filter: 'blur(4px)' },
        { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.6, stagger: 0.05, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        });
    });

    /* ── line-reveal ── */
    gsap.utils.toArray('[data-anim="line-reveal"]').forEach(function (el) {
      var line = splitLines(el);
      gsap.fromTo(line, { y: '100%' },
        { y: '0%', duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none none' }
        });
    });

    /* ── fade-up ── */
    gsap.utils.toArray('[data-anim="fade-up"]').forEach(function (el) {
      gsap.fromTo(el, { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        });
    });

    /* ── fade-left ── */
    gsap.utils.toArray('[data-anim="fade-left"]').forEach(function (el) {
      gsap.fromTo(el, { opacity: 0, x: -60 },
        { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        });
    });

    /* ── fade-right ── */
    gsap.utils.toArray('[data-anim="fade-right"]').forEach(function (el) {
      gsap.fromTo(el, { opacity: 0, x: 60 },
        { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        });
    });

    /* ── scale-in ── */
    gsap.utils.toArray('[data-anim="scale-in"]').forEach(function (el) {
      gsap.fromTo(el, { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1, duration: 0.7, ease: 'back.out(1.4)',
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        });
    });

    /* ── rotate-in ── */
    gsap.utils.toArray('[data-anim="rotate-in"]').forEach(function (el) {
      gsap.fromTo(el, { opacity: 0, scale: 0.9, rotation: -5 },
        { opacity: 1, scale: 1, rotation: 0, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        });
    });

    /* ── blur-in ── */
    gsap.utils.toArray('[data-anim="blur-in"]').forEach(function (el) {
      gsap.fromTo(el, { opacity: 0, filter: 'blur(20px)' },
        { opacity: 1, filter: 'blur(0px)', duration: 1, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        });
    });

    /* ── stagger ── */
    gsap.utils.toArray('[data-anim="stagger"]').forEach(function (p) {
      var items = p.querySelectorAll('[data-anim-item]');
      if (!items.length) return;
      gsap.fromTo(items, { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: 'power2.out',
          scrollTrigger: { trigger: p, start: 'top 85%', toggleActions: 'play none none none' }
        });
    });

    /* ── stagger-scale ── */
    gsap.utils.toArray('[data-anim="stagger-scale"]').forEach(function (p) {
      var items = p.querySelectorAll('[data-anim-item]');
      if (!items.length) return;
      gsap.fromTo(items, { opacity: 0, scale: 0.8, y: 20 },
        { opacity: 1, scale: 1, y: 0, duration: 0.5, stagger: 0.08, ease: 'back.out(1.4)',
          scrollTrigger: { trigger: p, start: 'top 85%', toggleActions: 'play none none none' }
        });
    });

    /* ── stagger-fade ── */
    gsap.utils.toArray('[data-anim="stagger-fade"]').forEach(function (p) {
      var items = p.querySelectorAll('[data-anim-item]');
      if (!items.length) return;
      gsap.fromTo(items, { opacity: 0 },
        { opacity: 1, duration: 0.5, stagger: 0.15, ease: 'power1.out',
          scrollTrigger: { trigger: p, start: 'top 85%', toggleActions: 'play none none none' }
        });
    });

    /* ── counter (supports K/M/B + fraction) ── */
    gsap.utils.toArray('[data-anim="counter"]').forEach(function (el) {
      var text = el.textContent.trim();
      var hasPlus = text.includes('+');
      var hasMinus = text.indexOf('-') === 0;
      var hasPercent = text.includes('%');
      var hasX = /x$/i.test(text);
      var hasK = /k/i.test(text);
      var hasM = /(?:^|[0-9])m/i.test(text);
      var hasB = /b$/i.test(text);
      var hasSlash = text.includes('/');

      var suffix = '';
      if (hasK) suffix += 'K';
      if (hasM) suffix += 'M';
      if (hasB) suffix += 'B';
      if (hasPlus) suffix += '+';
      if (hasPercent) suffix += '%';
      if (hasX) suffix += 'x';
      var prefix = hasMinus ? '-' : '';

      if (hasSlash) {
        var parts = text.split('/');
        var num = parseFloat(parts[0].replace(/[^0-9.]/g, '')) || 0;
        var den = parts[1].trim();
        var dec = parts[0].includes('.') ? parts[0].split('.')[1].replace(/[^0-9]/g, '').length : 0;
        var o = { v: 0 };
        gsap.to(o, { v: num, duration: 2, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' },
          onUpdate: function () { el.textContent = o.v.toFixed(dec) + '/' + den; }
        });
        return;
      }

      var cleaned = text.replace(/[^0-9.]/g, '');
      var end = parseFloat(cleaned) || 0;
      var dec2 = cleaned.includes('.') ? cleaned.split('.')[1].length : 0;
      var o2 = { v: 0 };
      gsap.to(o2, { v: end, duration: 2, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' },
        onUpdate: function () { el.textContent = prefix + o2.v.toFixed(dec2) + suffix; }
      });
    });

    /* ── draw-line (SVG) ── */
    gsap.utils.toArray('[data-anim="draw-line"]').forEach(function (el) {
      el.querySelectorAll('path, line, polyline, circle, rect').forEach(function (p) {
        var len = p.getTotalLength ? p.getTotalLength() : 100;
        gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
        gsap.to(p, { strokeDashoffset: 0, duration: 1.5, ease: 'power2.inOut',
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' }
        });
      });
    });

    /* ── parallax ── */
    gsap.utils.toArray('[data-anim="parallax"]').forEach(function (el) {
      var speed = parseFloat(el.dataset.speed) || 0.3;
      gsap.to(el, { y: function () { return -100 * speed; }, ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });

    /* ── parallax-bg ── */
    gsap.utils.toArray('[data-anim="parallax-bg"]').forEach(function (el) {
      gsap.to(el, { backgroundPositionY: '30%', ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });

    /* ── clip-reveal ── */
    gsap.utils.toArray('[data-anim="clip-reveal"]').forEach(function (el) {
      gsap.fromTo(el, { clipPath: 'inset(0 100% 0 0)' },
        { clipPath: 'inset(0 0% 0 0)', duration: 1, ease: 'power3.inOut',
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' }
        });
    });

    /* ── clip-reveal-up ── */
    gsap.utils.toArray('[data-anim="clip-reveal-up"]').forEach(function (el) {
      gsap.fromTo(el, { clipPath: 'inset(100% 0 0 0)' },
        { clipPath: 'inset(0% 0 0 0)', duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' }
        });
    });

    /* ── image-reveal (directional mask) ── */
    gsap.utils.toArray('[data-image-reveal]').forEach(function (el) {
      var d = el.dataset.imageReveal || 'left';
      var f, t;
      switch (d) {
        case 'right':  f = 'inset(0 0 0 100%)'; t = 'inset(0 0 0 0%)'; break;
        case 'top':    f = 'inset(0 0 100% 0)';  t = 'inset(0 0 0% 0)'; break;
        case 'bottom': f = 'inset(100% 0 0 0)';  t = 'inset(0% 0 0 0)'; break;
        default:       f = 'inset(0 100% 0 0)';  t = 'inset(0 0% 0 0)';
      }
      gsap.fromTo(el, { clipPath: f },
        { clipPath: t, duration: 1, ease: 'power3.inOut',
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' }
        });
    });

    /* ── image-scale (scrubbed subtle zoom out) ── */
    gsap.utils.toArray('[data-anim="image-scale"]').forEach(function (el) {
      gsap.fromTo(el, { scale: 1.08 },
        { scale: 1, ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
        });
    });
  }

  /* ================================================================
   * MODULE 6 — MARQUEE
   * ================================================================ */
  function initMarquee(gsap) {
    /* Marquee is now handled by pure CSS animation for seamless looping */
    return;
  }

  /* ================================================================
   * MODULE 7 — HOVER INTERACTIONS
   * ================================================================ */
  function initInteractions(gsap) {

    /* magnetic */
    gsap.utils.toArray('[data-magnetic]').forEach(function (el) {
      var str = parseFloat(el.dataset.magnetic) || 0.3;
      var xTo = gsap.quickTo(el, 'x', { duration: 0.5, ease: 'power3.out' });
      var yTo = gsap.quickTo(el, 'y', { duration: 0.5, ease: 'power3.out' });
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        xTo((e.clientX - r.left - r.width / 2) * str);
        yTo((e.clientY - r.top - r.height / 2) * str);
      });
      el.addEventListener('mouseleave', function () { xTo(0); yTo(0); });
    });

    /* tilt */
    gsap.utils.toArray('[data-tilt]').forEach(function (el) {
      var max = parseFloat(el.dataset.tilt) || 10;
      var glare = el.querySelector('.tilt-glare');
      el.style.transformStyle = 'preserve-3d';
      el.style.perspective = '1000px';
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width;
        var y = (e.clientY - r.top) / r.height;
        gsap.to(el, { rotateX: (y - 0.5) * -max * 2, rotateY: (x - 0.5) * max * 2, duration: 0.3, ease: 'power2.out' });
        if (glare) gsap.to(glare, { x: x * 100 + '%', y: y * 100 + '%', opacity: 0.15, duration: 0.3 });
      });
      el.addEventListener('mouseleave', function () {
        gsap.to(el, { rotateX: 0, rotateY: 0, duration: 0.5, ease: 'power3.out' });
        if (glare) gsap.to(glare, { opacity: 0, duration: 0.3 });
      });
    });

    /* hover-scale */
    gsap.utils.toArray('[data-hover-scale]').forEach(function (el) {
      var s = parseFloat(el.dataset.hoverScale) || 1.05;
      el.addEventListener('mouseenter', function () { gsap.to(el, { scale: s, duration: 0.3, ease: 'power2.out' }); });
      el.addEventListener('mouseleave', function () { gsap.to(el, { scale: 1, duration: 0.3, ease: 'power2.out' }); });
    });

    /* hover-glow */
    gsap.utils.toArray('[data-hover-glow]').forEach(function (el) {
      var c = el.dataset.hoverGlow || 'rgba(59, 130, 246, 0.4)';
      el.addEventListener('mouseenter', function () { gsap.to(el, { boxShadow: '0 0 30px ' + c, duration: 0.3 }); });
      el.addEventListener('mouseleave', function () { gsap.to(el, { boxShadow: '0 0 0px transparent', duration: 0.3 }); });
    });
  }

  /* ================================================================
   * MODULE 8 — NAVIGATION (hide/show + shrink)
   * ================================================================ */
  function initNavigation(gsap, ScrollTrigger) {
    var header = document.querySelector('.header-wrapper');
    if (!header) return;
    var h = header.offsetHeight;

    ScrollTrigger.create({
      start: 'top -80',
      onUpdate: function (self) {
        var scrollY = self.scroll();
        if (scrollY > 200) { header.classList.add('is-shrunk'); }
        else { header.classList.remove('is-shrunk'); }
      }
    });
  }

  /* ================================================================
   * MODULE 9 — LAZY BLUR REVEAL
   * ================================================================ */
  function initBlurReveal() {
    document.querySelectorAll('.agency-img-blur').forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', function () { img.classList.add('is-loaded'); });
      }
    });
  }

  /* ================================================================
   * MODULE 10 — SCROLL-LINKED VIDEO
   * ================================================================ */
  function initScrollVideo(gsap, ScrollTrigger) {
    gsap.utils.toArray('[data-scroll-video]').forEach(function (section) {
      var video = section.querySelector('[data-scroll-video-el]');
      if (!video) return;
      function bind() {
        ScrollTrigger.create({
          trigger: section, start: 'top 80%', end: 'bottom 20%', scrub: 0.5,
          onUpdate: function (self) {
            if (video.duration) video.currentTime = video.duration * self.progress;
          }
        });
      }
      if (video.readyState >= 1) bind();
      else video.addEventListener('loadedmetadata', bind);
    });
  }

  /* ================================================================
   * MODULE 11 — SECTION-SPECIFIC
   * ================================================================ */
  function initSectionSpecific(gsap, ScrollTrigger) {

    /* Process steps */
    var proc = document.querySelector('.process-pinned');
    if (proc) {
      var steps = proc.querySelectorAll('[data-process-step]');
      steps.forEach(function (step) {
        ScrollTrigger.create({
          trigger: step, start: 'top center', end: 'bottom center',
          onEnter: function () { steps.forEach(function (s) { s.classList.remove('is-active'); }); step.classList.add('is-active'); },
          onEnterBack: function () { steps.forEach(function (s) { s.classList.remove('is-active'); }); step.classList.add('is-active'); }
        });
      });
      if (steps.length) steps[0].classList.add('is-active');
    }

    /* Horizontal scroll */
    var hSec = document.querySelector('.services-horizontal');
    if (hSec) {
      var hTrack = hSec.querySelector('.services-horizontal__track');
      if (hTrack) {
        var tl = gsap.timeline({
          scrollTrigger: { trigger: hSec, start: 'top top', end: '+=200%', pin: true, scrub: 1 }
        });
        var w = hTrack.scrollWidth - (window.innerWidth || document.documentElement.clientWidth);
        if (w > 0) tl.to(hTrack, { x: -w, ease: 'none' });
      }
    }

    /* ── Homepage Sticky Stack — DISABLED for Neo Brutalism (flat layout) ── */

    /* Service cards hover */
    gsap.utils.toArray('.agency-service-capabilities__card').forEach(function (c) {
      c.addEventListener('mouseenter', function () { gsap.to(c, { y: -8, scale: 1.02, duration: 0.3, ease: 'power2.out' }); });
      c.addEventListener('mouseleave', function () { gsap.to(c, { y: 0, scale: 1, duration: 0.3, ease: 'power2.out' }); });
    });

    /* Service hero stats */
    gsap.utils.toArray('.agency-service-hero__stat').forEach(function (s, i) {
      gsap.fromTo(s, { opacity: 0, y: 20, scale: 0.9 },
        { opacity: 1, y: 0, scale: 1, duration: 0.5, delay: i * 0.1, ease: 'back.out(1.4)',
          scrollTrigger: { trigger: s, start: 'top 90%', toggleActions: 'play none none none' }
        });
    });

    /* Process connector */
    var pSteps = document.querySelector('.agency-service-process__steps');
    if (pSteps) {
      var conn = pSteps.querySelector('.process-connector');
      if (conn) {
        gsap.fromTo(conn, { scaleY: 0, transformOrigin: 'top center' },
          { scaleY: 1, duration: 1.5, ease: 'power2.out',
            scrollTrigger: { trigger: pSteps, start: 'top 70%', toggleActions: 'play none none none' }
          });
      }
    }

    /* FAQ accordion */
    document.querySelectorAll('.agency-service-faq__item').forEach(function (item) {
      var ans = item.querySelector('.agency-service-faq__answer');
      if (!ans) return;
      item.addEventListener('toggle', function () {
        if (item.open) gsap.fromTo(ans, { height: 0, opacity: 0 }, { height: 'auto', opacity: 1, duration: 0.3, ease: 'power2.out' });
      });
    });
  }

  /* ================================================================
   * MAIN RUNNERS
   * ================================================================ */
  function runGlobalEffects(gsap) {
    if (window.__agencyGlobalInit) return;
    window.__agencyGlobalInit = true;
    initPreloader(gsap);
    initCursor(gsap);
    initBlurReveal();
  }

  function runAnimations() {
    if (window.__agencyAnimInit) return;
    window.__agencyAnimInit = true;

    var gsap = window.gsap;
    var ST = window.ScrollTrigger;
    if (!gsap || !ST) return;

    gsap.registerPlugin(ST);
    gsap.defaults({ ease: 'power3.out' });

    initLenis(gsap, ST);

    if (reducedMotion) {
      gsap.utils.toArray('[data-anim]').forEach(function (el) { gsap.set(el, { opacity: 1 }); });
      runGlobalEffects(gsap);
      return;
    }

    runGlobalEffects(gsap);
    initScrollProgress(gsap, ST);
    initScrollAnimations(gsap, ST);
    initMarquee(gsap);
    initInteractions(gsap);
    initNavigation(gsap, ST);
    initScrollVideo(gsap, ST);
    initSectionSpecific(gsap, ST);

    ST.refresh();
    if (document.querySelector('section[class*="agency-service-"]')) {
      window.addEventListener('load', function r() { ST.refresh(); window.removeEventListener('load', r); });
      setTimeout(ST.refresh, 400);
    }
  }

  /* ================================================================
   * INITIALIZATION
   * ================================================================ */
  function init() {
    var pre = document.querySelector('[data-preloader]');
    if (pre) {
      setTimeout(function () {
        if (!pre.classList.contains('is-hidden')) pre.classList.add('is-hidden');
      }, 3000);
    }

    if (window.gsap && window.ScrollTrigger) { runAnimations(); return; }
    Promise.all([loadScript(CDN_GSAP), loadScript(CDN_ST)])
      .then(function () { return loadScript(CDN_LEN).catch(function () {}); })
      .then(runAnimations)
      .catch(function () { window.__agencyAnimInit = false; });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ================================================================
   * THEME EDITOR RE-INIT
   * ================================================================ */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      window.__agencyAnimInit = false;
      if (window.gsap && window.ScrollTrigger) {
        window.ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
        runAnimations();
      } else {
        setTimeout(function () { window.__agencyAnimInit = false; runAnimations(); }, 100);
      }
    });
  }
})();
